import React, { useState } from 'react';
import { 
  X, Package, MapPin, Phone, User, Calendar, DollarSign, 
  Truck, CheckCircle, XCircle, Clock, Image as ImageIcon, AlertCircle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL;

function SalesOrderModal({ order, onClose, onUpdate }) {
  const [updating, setUpdating] = useState(false);
  const [newStatus, setNewStatus] = useState(order.status);
  const [trackingCode, setTrackingCode] = useState(order.provincia_tracking_code || '');
  const [showImageModal, setShowImageModal] = useState(false);

  const handleUpdateStatus = async () => {
    if (newStatus === order.status) return;

    setUpdating(true);
    try {
      const response = await fetch(`${API_URL}/api/sales/orders/${order.id}/update-status`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          status: newStatus,
          tracking_code: trackingCode || undefined
        })
      });

      const data = await response.json();
      if (data.success) {
        alert('Estado actualizado correctamente');
        onUpdate();
        onClose();
      } else {
        alert('Error: ' + data.error);
      }
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Error al actualizar estado');
    } finally {
      setUpdating(false);
    }
  };

  const handleConfirmPrepayment = async () => {
    if (!window.confirm('¿Confirmar que el pago de S/20 es válido?')) return;

    setUpdating(true);
    try {
      const response = await fetch(`${API_URL}/api/sales/orders/${order.id}/confirm-prepayment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ admin_user_id: 1 })
      });

      const data = await response.json();
      if (data.success) {
        alert('Pago confirmado correctamente');
        onUpdate();
        onClose();
      } else {
        alert('Error: ' + data.error);
      }
    } catch (error) {
      console.error('Error confirming payment:', error);
      alert('Error al confirmar pago');
    } finally {
      setUpdating(false);
    }
  };

  const handleRejectPrepayment = async () => {
    const reason = prompt('¿Por qué rechazar el comprobante?', 'Comprobante no válido');
    if (!reason) return;

    setUpdating(true);
    try {
      const response = await fetch(`${API_URL}/api/sales/orders/${order.id}/reject-prepayment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason })
      });

      const data = await response.json();
      if (data.success) {
        alert('Comprobante rechazado');
        onUpdate();
        onClose();
      }
    } catch (error) {
      console.error('Error rejecting payment:', error);
      alert('Error al rechazar comprobante');
    } finally {
      setUpdating(false);
    }
  };

  const statusOptions = [
    { value: 'pending', label: 'Pendiente', icon: Clock },
    { value: 'confirmed', label: 'Confirmado', icon: CheckCircle },
    { value: 'preparing', label: 'Preparando', icon: Package },
    { value: 'ready_for_delivery', label: 'Listo para Envío', icon: Package },
    { value: 'shipped_to_courier', label: 'Enviado a Courier', icon: Truck },
    { value: 'in_transit', label: 'En Tránsito', icon: Truck },
    { value: 'delivered', label: 'Entregado', icon: CheckCircle },
    { value: 'cancelled', label: 'Cancelado', icon: XCircle }
  ];

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('es-PE', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-gray-900">
                Orden {order.order_number}
              </h2>
              <p className="text-sm text-gray-500">
                Creada: {formatDate(order.created_at)}
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-gray-600" />
            </button>
          </div>

          <div className="p-6 space-y-6">
            {/* Customer Info */}
            <div>
              <h3 className="font-semibold text-gray-900 mb-3 flex items-center">
                <User className="w-5 h-5 mr-2 text-[#B1336E]" />
                Información del Cliente
              </h3>
              <div className="grid grid-cols-2 gap-4 bg-gray-50 p-4 rounded-lg">
                <div>
                  <p className="text-xs text-gray-500">Nombre</p>
                  <p className="font-medium">{order.customer_name}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Teléfono</p>
                  <p className="font-medium">{order.customer_phone}</p>
                </div>
                {order.customer_dni && (
                  <div>
                    <p className="text-xs text-gray-500">DNI</p>
                    <p className="font-medium">{order.customer_dni}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Delivery Info */}
            <div>
              <h3 className="font-semibold text-gray-900 mb-3 flex items-center">
                <MapPin className="w-5 h-5 mr-2 text-[#B1336E]" />
                Información de Entrega
              </h3>
              <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                <div>
                  <p className="text-xs text-gray-500">Tipo</p>
                  <p className="font-medium">
                    {order.delivery_type === 'lima' ? '📦 Lima' : '🚚 Provincia'}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Dirección</p>
                  <p className="font-medium">{order.delivery_address}</p>
                </div>
                {order.delivery_district && (
                  <div>
                    <p className="text-xs text-gray-500">Distrito</p>
                    <p className="font-medium">{order.delivery_district}</p>
                  </div>
                )}
                {order.delivery_city && (
                  <div>
                    <p className="text-xs text-gray-500">Ciudad</p>
                    <p className="font-medium">{order.delivery_city}, {order.delivery_province}</p>
                  </div>
                )}
                {order.delivery_reference && (
                  <div>
                    <p className="text-xs text-gray-500">Referencia</p>
                    <p className="font-medium">{order.delivery_reference}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Order Items */}
            <div>
              <h3 className="font-semibold text-gray-900 mb-3 flex items-center">
                <Package className="w-5 h-5 mr-2 text-[#B1336E]" />
                Productos ({order.items?.length || 0})
              </h3>
              <div className="space-y-2">
                {order.items?.map((item, index) => (
                  <div key={index} className="flex justify-between items-center bg-gray-50 p-3 rounded-lg">
                    <div>
                      <p className="font-medium text-sm">{item.product_title}</p>
                      <p className="text-xs text-gray-500">SKU: {item.product_sku}</p>
                      <p className="text-xs text-gray-600">Cantidad: {item.quantity}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-[#B1336E]">
                        S/ {parseFloat(item.subtotal).toFixed(2)}
                      </p>
                      <p className="text-xs text-gray-500">
                        S/ {parseFloat(item.unit_price).toFixed(2)} c/u
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Pricing */}
            <div className="bg-purple-50 p-4 rounded-lg">
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Subtotal:</span>
                  <span className="font-medium">S/ {parseFloat(order.subtotal).toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Envío:</span>
                  <span className="font-medium">S/ {parseFloat(order.shipping_cost).toFixed(2)}</span>
                </div>
                <div className="border-t border-purple-200 pt-2 flex justify-between">
                  <span className="font-semibold text-gray-900">Total:</span>
                  <span className="font-bold text-xl text-[#B1336E]">
                    S/ {parseFloat(order.total_amount).toFixed(2)}
                  </span>
                </div>

                {order.requires_prepayment && (
                  <>
                    <div className="border-t border-purple-200 pt-2 flex justify-between text-sm">
                      <span className="text-gray-600">Adelanto (Reserva):</span>
                      <span className="font-medium text-yellow-600">
                        S/ {parseFloat(order.prepayment_amount).toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Saldo al Recibir:</span>
                      <span className="font-medium text-green-600">
                        S/ {parseFloat(order.remaining_balance).toFixed(2)}
                      </span>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Prepayment Section */}
            {order.requires_prepayment && (
              <div>
                <h3 className="font-semibold text-gray-900 mb-3 flex items-center">
                  <DollarSign className="w-5 h-5 mr-2 text-[#B1336E]" />
                  Estado de Adelanto
                </h3>
                <div className={`p-4 rounded-lg ${
                  order.prepayment_status === 'confirmed' 
                    ? 'bg-green-50 border border-green-200' 
                    : 'bg-yellow-50 border border-yellow-200'
                }`}>
                  <div className="flex items-center justify-between mb-3">
                    <span className="font-medium">
                      {order.prepayment_status === 'confirmed' 
                        ? '✅ Pago Confirmado' 
                        : '⏳ Esperando Confirmación'}
                    </span>
                    {order.prepayment_confirmed_at && (
                      <span className="text-xs text-gray-600">
                        {formatDate(order.prepayment_confirmed_at)}
                      </span>
                    )}
                  </div>

                  {order.prepayment_proof_url && (
                    <div className="mb-3">
                      <p className="text-xs text-gray-600 mb-2">Comprobante:</p>
                      <button
                        onClick={() => setShowImageModal(true)}
                        className="relative w-full h-48 bg-gray-100 rounded-lg overflow-hidden hover:opacity-90 transition-opacity"
                      >
                        <img
                          src={order.prepayment_proof_url}
                          alt="Comprobante"
                          className="w-full h-full object-contain"
                        />
                        <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-0 hover:bg-opacity-20 transition-all">
                          <ImageIcon className="w-8 h-8 text-white opacity-0 hover:opacity-100" />
                        </div>
                      </button>
                    </div>
                  )}

                  {order.prepayment_status === 'pending' && order.prepayment_proof_url && (
                    <div className="flex space-x-2">
                      <button
                        onClick={handleConfirmPrepayment}
                        disabled={updating}
                        className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 font-medium"
                      >
                        ✓ Confirmar Pago
                      </button>
                      <button
                        onClick={handleRejectPrepayment}
                        disabled={updating}
                        className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 font-medium"
                      >
                        ✗ Rechazar
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Status Update */}
            <div>
              <h3 className="font-semibold text-gray-900 mb-3 flex items-center">
                <Truck className="w-5 h-5 mr-2 text-[#B1336E]" />
                Actualizar Estado
              </h3>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Estado de la Orden
                  </label>
                  <select
                    value={newStatus}
                    onChange={(e) => setNewStatus(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  >
                    {statusOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                {order.delivery_type === 'provincia' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Código de Rastreo (Shalom)
                    </label>
                    <input
                      type="text"
                      value={trackingCode}
                      onChange={(e) => setTrackingCode(e.target.value)}
                      placeholder="SHA-123456"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    />
                  </div>
                )}

                <button
                  onClick={handleUpdateStatus}
                  disabled={updating || newStatus === order.status}
                  className="w-full px-4 py-3 bg-[#B1336E] text-white rounded-lg hover:bg-[#8f2858] disabled:opacity-50 font-medium"
                >
                  {updating ? 'Actualizando...' : 'Actualizar Estado'}
                </button>
              </div>
            </div>

            {/* Notes */}
            {order.notes && (
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Notas</h3>
                <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded-lg">
                  {order.notes}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Image Modal */}
      {showImageModal && order.prepayment_proof_url && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-[60] p-4"
          onClick={() => setShowImageModal(false)}
        >
          <div className="relative max-w-4xl max-h-full">
            <button
              onClick={() => setShowImageModal(false)}
              className="absolute top-4 right-4 p-2 bg-white rounded-full hover:bg-gray-100"
            >
              <X className="w-6 h-6" />
            </button>
            <img
              src={order.prepayment_proof_url}
              alt="Comprobante"
              className="max-w-full max-h-[90vh] object-contain"
            />
          </div>
        </div>
      )}
    </>
  );
}

export default SalesOrderModal;